import { Dispatch, SetStateAction, useCallback, useState } from "react";

import type { PagedResult } from "@king-ict/eupisi-ui/models";

import type {
  ValidationFailure,
  Selectable,
  SaveSchoolCandidateInstitutionAssignment,
  SchoolCandidateResidenceChange,
} from "../../../../models";
import { buildTableDataWithSelection } from "../util";
import { partitionSelectedByNewAddress } from "./utils";
import type {
  DownloadAddressesRequest,
  MissingAddressWarningState,
} from "./types";

interface IProps {
  countyId?: number;
  batchId: number | null;
  isSchoolYearEditable: boolean;
  tableData: PagedResult<SchoolCandidateResidenceChange & Selectable>;
  setTableData: Dispatch<
    SetStateAction<PagedResult<SchoolCandidateResidenceChange & Selectable>>
  >;
  updateInstitutionAssignment: SaveSchoolCandidateInstitutionAssignment;
  setUpdateInstitutionAssignment: Dispatch<
    SetStateAction<SaveSchoolCandidateInstitutionAssignment>
  >;
  downloadAddressesRequest: DownloadAddressesRequest;
  setErrors: Dispatch<SetStateAction<ValidationFailure[] | undefined>>;
  setConfirmUnassign: Dispatch<
    SetStateAction<"confirm" | "message" | "error" | undefined>
  >;
  setAssignmentButtons: (
    update: SaveSchoolCandidateInstitutionAssignment
  ) => void;
}

export const useDownloadAddresses = ({
  countyId,
  batchId,
  isSchoolYearEditable,
  tableData,
  setTableData,
  updateInstitutionAssignment,
  setUpdateInstitutionAssignment,
  downloadAddressesRequest,
  setErrors,
  setConfirmUnassign,
  setAssignmentButtons,
}: IProps) => {
  const [missingAddressWarning, setMissingAddressWarning] =
    useState<MissingAddressWarningState | null>(null);

  const handleDownloadAddressesClicked = useCallback(async () => {
    const { schoolCandidateList } = updateInstitutionAssignment;

    if (!schoolCandidateList.length) {
      return;
    }

    if (!isSchoolYearEditable || !countyId || !batchId) {
      return;
    }

    try {
      const result = await downloadAddressesRequest({
        countyId,
        batchId,
        schoolCandidateIds: schoolCandidateList,
      }).unwrap();

      setErrors(result.validationFailures);
      window.scroll(0, 0);

      if (result.isValid) {
        setConfirmUnassign("message");
      } else {
        setConfirmUnassign(undefined);
      }
    } catch {
      setErrors(undefined);
      setConfirmUnassign("error");
    }
  }, [
    batchId,
    countyId,
    downloadAddressesRequest,
    isSchoolYearEditable,
    setConfirmUnassign,
    setErrors,
    updateInstitutionAssignment,
  ]);

  const handleConfirmDownloadAddresses = useCallback(() => {
    const { schoolCandidateList } = updateInstitutionAssignment;

    if (
      !schoolCandidateList.length ||
      !isSchoolYearEditable ||
      !countyId ||
      !batchId
    ) {
      setConfirmUnassign(undefined);
      return;
    }

    const selectedIdSet = new Set(schoolCandidateList);
    const selectedCandidates = tableData.results.filter((candidate) =>
      selectedIdSet.has(candidate.schoolCandidateId)
    );
    const { invalidCandidates, validIds } =
      partitionSelectedByNewAddress(selectedCandidates);

    setConfirmUnassign(undefined);

    if (invalidCandidates.length > 0) {
      setMissingAddressWarning({ invalidCandidates, validIds });
      return;
    }

    handleDownloadAddressesClicked();
  }, [
    batchId,
    countyId,
    handleDownloadAddressesClicked,
    isSchoolYearEditable,
    setConfirmUnassign,
    tableData.results,
    updateInstitutionAssignment,
  ]);

  const handleProceedMissingAddress = useCallback(async () => {
    if (!missingAddressWarning || missingAddressWarning.validIds.length === 0) {
      setMissingAddressWarning(null);
      return;
    }

    const { validIds } = missingAddressWarning;

    setMissingAddressWarning(null);

    const nextTableData = buildTableDataWithSelection(tableData, validIds);
    const nextUpdate: SaveSchoolCandidateInstitutionAssignment = {
      ...updateInstitutionAssignment,
      schoolCandidateList: validIds,
    };

    setTableData(nextTableData);
    setUpdateInstitutionAssignment(nextUpdate);
    setAssignmentButtons(nextUpdate);

    if (!isSchoolYearEditable || !countyId || !batchId) {
      return;
    }

    try {
      const result = await downloadAddressesRequest({
        countyId,
        batchId,
        schoolCandidateIds: validIds,
      }).unwrap();

      setErrors(result.validationFailures);
      window.scroll(0, 0);

      if (result.isValid) {
        setConfirmUnassign("message");
      } else {
        setConfirmUnassign("error");
      }
    } catch {
      setErrors(undefined);
      setConfirmUnassign("error");
    }
  }, [
    batchId,
    countyId,
    downloadAddressesRequest,
    isSchoolYearEditable,
    missingAddressWarning,
    setAssignmentButtons,
    setConfirmUnassign,
    setErrors,
    setTableData,
    setUpdateInstitutionAssignment,
    tableData,
    updateInstitutionAssignment,
  ]);

  const handleCloseMissingAddressWarning = useCallback(() => {
    setMissingAddressWarning(null);
  }, []);

  return {
    missingAddressWarning,
    handleConfirmDownloadAddresses,
    handleProceedMissingAddress,
    handleCloseMissingAddressWarning,
  };
};
